import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';

import { AuthService } from './auth.service';
import { AuthRoutePolicy, ProviderPortalKind } from '@app/core/models/portal-session.model';

@Injectable()
export class AuthGuardService implements CanActivate {
  constructor(public auth: AuthService, public router: Router) {}

  canActivate(route: ActivatedRouteSnapshot): boolean {
    if (!this.auth.getToken()) {
      void this.router.navigate(['/auth/login']);
      return false;
    }

    const policy = this.resolveAuthPolicy(route);

    if (policy === 'STORE' && !this.auth.isStoreSession()) {
      if (this.auth.isProviderSession()) {
        void this.router.navigate(this.auth.providerHomeUrl());
      } else {
        void this.router.navigate(['/auth/login']);
      }
      return false;
    }

    if (policy === 'PROVIDER' && !this.auth.isProviderSession()) {
      if (this.auth.isStoreSession()) {
        void this.router.navigate(['/dashboard']);
      } else {
        void this.router.navigate(['/auth/login']);
      }
      return false;
    }

    // Taxi office and bus operator are both provider sessions; each may only open its own portal
    // (the other portal's API would answer 401 and sign the user out).
    const portal = this.resolveProviderPortal(route);
    if (policy === 'PROVIDER' && portal && this.auth.getProviderPortalKind() !== portal) {
      void this.router.navigate(this.auth.providerHomeUrl());
      return false;
    }

    return true;
  }

  private resolveProviderPortal(route: ActivatedRouteSnapshot): ProviderPortalKind | undefined {
    let node: ActivatedRouteSnapshot | null = route;
    while (node) {
      const portal = node.data?.['providerPortal'] as ProviderPortalKind | undefined;
      if (portal) {
        return portal;
      }
      node = node.firstChild;
    }
    return undefined;
  }

  private resolveAuthPolicy(route: ActivatedRouteSnapshot): AuthRoutePolicy {
    let leaf: ActivatedRouteSnapshot | null = route;
    while (leaf?.firstChild) {
      leaf = leaf.firstChild;
    }
    const fromData = leaf?.data?.['authPolicy'] as AuthRoutePolicy | undefined;
    if (fromData === 'STORE' || fromData === 'PROVIDER') {
      return fromData;
    }
    return 'STORE';
  }
}
